
import {
    defineComponent,
    ref,
    onMounted,
    reactive,
    markRaw,
    computed,
    toRefs,
    watch,
    toRef
} from "vue";
import CigoIconFont from "@/components/cigo-ui/unit/basic/cigo-icon-font.vue";
import CigoMask from "./mask.vue";
import cigoLayer from "@/components/cigo-layer/index";
import {
    TweenMax
} from "gsap";
import {
    debounce
} from "lodash";

export default defineComponent({
    name: "CigoComponent",
    components: {
        CigoMask,
        CigoIconFont
    },
    props: {
        layerIndex: {
            type: Number,
            default: ""
        },
        component: {
            type: Object,
            default: {}
        },
        width: {
            type: String,
            default: "500px"
        },
        height: {
            type: String,
            default: "400px"
        },
        showX: {
            type: Object,
            default: null
        },
        showY: {
            type: Object,
            default: null
        },
        componentSize: {
            type: String,
            default: "common"
        },
        showCtrlBar: {
            type: Boolean,
            default: true
        },
        canDrag: {
            type: Boolean,
            default: true
        },
        backgroundColor: {
            type: String,
            default: "#fff"
        },
        maskClose: {
            type: Boolean,
            default: true
        },
        showMask: {
            type: Boolean,
            default: true
        },
        layerData: {
            type: Object,
            default: {}
        },
        notify: {
            type: Function,
            default: () => {}
        }
    },
    setup(props) {
        let showXRef = toRef(props, "showX");
        let showYRef = toRef(props, "showY");

        let componentRaw = computed(() => {
            return markRaw(props.component);
        });
        onMounted(() => {
            initDragView();
            watchShowPos();
            winResize();
        });
        let ctrlBarMoveBtnRef: any = ref(null);
        let componentContainerRef: any = ref(null);
        const initDragView = () => {
            if (props.layerData.dragElemRef) {
                props.layerData.dragElemRef.value.addEventListener(
                    "mousedown",
                    startDrag
                );
            } else {
                componentContainerRef.value.addEventListener(
                    "mousedown",
                    startDrag
                );
            }
            if (props.showCtrlBar) {
                //TODO 检查普通dom和自定义组件通过ref获取组件的refImpl区别
                ctrlBarMoveBtnRef.value.addEventListener(
                    "mousedown",
                    startDrag
                );
            }
        };
        const watchShowPos = () => {
            if (props.showX === null || props.showY === null) {
                return;
            }
            watch([showXRef, showYRef], ([xNew, yNew], [xPre, yPre]) => {
                delayChangeShowPos(xNew, yNew);
            });
        };
        const delayChangeShowPos = debounce((xPos, yPos) => {
            TweenMax.to("#component-container-" + props.layerIndex, 0.5, {
                left: xPos + "px",
                top: yPos + "px",
                onComplete: () => {
                    moveX.value = xPos;
                    moveY.value = yPos;
                }
            });
        }, 80);

        let componentSizeRef = ref(props.componentSize);
        const changeWinSie = (size: string) => {
            componentSizeRef.value = size;
        };
        watch(componentSizeRef, () => {
            moveX.value =
                props.showX === null || props.showY === null ?
                0 :
                props.showX.value;
            moveY.value =
                props.showX === null || props.showY === null ?
                0 :
                props.showY.value;
            dragedX = 0;
            dragedY = 0;
            winResize();
        });
        let showOptions = computed(() => {
            // 1. 初始化mask动画options
            let maskOption = {};
            let maskOpacity = props.showMask ? 0.6 : 0;

            //2.组装component动画options
            let componentOption = {};
            let contentOpacity = 1;
            let contentWidth = props.width;
            let contentHeight = props.height;

            //3. 根据窗口状态动态修改属性
            switch (componentSizeRef.value) {
                case "max":
                    contentWidth = "100%";
                    contentHeight = "100%";
                    break;
                case "min":
                    maskOpacity = 0;
                    contentWidth = "0px";
                    contentHeight = "0px";

                    break;
                case "common":
                default:
                    break;
            }

            //4.拼接修改
            Object.assign(maskOption, {
                opacity: maskOpacity
            });
            Object.assign(componentOption, {
                opacity: contentOpacity,
                width: contentWidth,
                height: contentHeight
            });

            return {
                mask: maskOption,
                content: componentOption
            };
        });
        const winResize = () => {
            TweenMax.to(
                "#component-mask-" + props.layerIndex,
                0.5,
                showOptions.value.mask
            );
            TweenMax.to(
                "#component-container-" + props.layerIndex,
                0.5,
                showOptions.value.content
            );
        };

        const notifyClose = () => {
            delayClose();
        };
        const clickClose = () => {
            delayClose();
        };
        const delayClose = () => {
            TweenMax.to("#component-mask-" + props.layerIndex, 0.5, {
                opacity: 0
            });
            TweenMax.to("#component-container-" + props.layerIndex, 0.5, {
                opacity: 0,
                width: "0px",
                height: "0px",
                onComplete: () => {
                    cigoLayer.close(props.layerIndex);
                }
            });
        };
        const clickMask = () => {
            props.maskClose && delayClose(); //Tips_Flag &&创新用法
        };

        let moveX = ref(
            props.showX === null || props.showY === null ? 0 : props.showX.value
        );
        let moveY = ref(
            props.showX === null || props.showY === null ? 0 : props.showY.value
        );
        let dragedX = 0;
        let dragedY = 0;
        let dragStarted = false;
        let dragging = ref(false);
        let startX = 0;
        let startY = 0;
        const startDrag = (e: any) => {
            if (!props.canDrag) {
                return;
            }
            dragStarted = true;
            startX = e.pageX;
            startY = e.pageY;
            dragedX = moveX.value;
            dragedY = moveY.value;

            window.addEventListener("mousemove", mouseMoving);
            window.addEventListener("touchmove", mouseMoving);
            window.addEventListener("mouseup", stopDrag);
            window.addEventListener("touchend", stopDrag);
            window.addEventListener("contextmenu", stopDrag);
        };
        const mouseMoving = (e: any) => {
            if (!props.canDrag) {
                return;
            }
            if (!dragStarted) {
                return;
            }
            let xMoveCurr = e.pageX - startX;
            let yMoveCurr = e.pageY - startY;

            // 开启移动
            if (Math.abs(xMoveCurr) > 4 || Math.abs(yMoveCurr) > 4) {
                dragging.value = true;
            }
            if (dragging.value) {
                moveX.value = xMoveCurr + dragedX;
                moveY.value = yMoveCurr + dragedY;
            }
        };

        let targetX = ref(Math.round(moveX.value) + "px");
        let targetY = ref(Math.round(moveY.value) + "px");
        watch([moveX, moveY], ([xNew, yNew], [xPre, yPre]) => {
            targetX.value = xNew + "px";
            targetY.value = yNew + "px";
        });

        const stopDrag = () => {
            if (!props.canDrag) {
                return;
            }
            dragStarted = false;
            dragging.value = false;

            window.removeEventListener("mousemove", mouseMoving);
            window.removeEventListener("touchmove", mouseMoving);
            window.removeEventListener("mouseup", stopDrag);
            window.removeEventListener("touchend", stopDrag);
            window.removeEventListener("contextmenu", stopDrag);
        };
        return {
            ctrlBarMoveBtnRef,
            componentContainerRef,
            componentRaw,
            changeWinSie,
            componentSizeRef,
            targetX,
            targetY,
            clickMask,
            notifyClose,
            clickClose,
            delayClose,
            dragging
        };
    }
});
